/**
 * Modal de progression pour la facturation des contrats de location.
 *
 * Orchestration :
 * 1. Présente les contrats éligibles certifiés par le backend (prepare_invoicing)
 * 2. Démarre automatiquement la sécurisation séquentielle Area pour les contrats FSE
 * 3. Affiche en temps réel le statut de chaque ligne (En attente, En cours, Succès avec N° FSE, Échec avec motif)
 * 4. Finalise l'événement RENTAL_INVOICING côté backend uniquement pour les contrats ayant réussi
 * 5. Présente le bilan final et permet la fermeture pour rafraîchir la liste parent
 *
 * Dépendances :
 * - RentalFseService (sécurisation Area unitaire)
 * - QuotationsService (finalisation RENTAL_INVOICING backend)
 * - LoginService (userId)
 * - MatDialogRef / MAT_DIALOG_DATA
 */

import { Component, OnInit, inject, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule, MatCheckboxChange } from '@angular/material/checkbox';
import { RouterLink } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';

import {
  PrepareRentalInvoicingResponse,
  PreparedRentalContractInvoice,
  PreparedRentalCredit,
  PreparedRentalSimulationLine,
  PreparedSinglePeriodInfo,
  UnbillableRentalContract,
  QuotationsService,
  RentalAnomaly,
} from '@app/sales/quotations/quotations.service';
import { RentalFseService, FseOrdersMapping } from '@app/rental/rental-fse.service';
import { LoginService } from '@app/common/login.service';
import { businessDayOf, pickedDateStr, todayStr } from '@app/shared/time';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { isPlainLeftClick } from '@app/shared/util';
import { frDay, frShort, money, qtyFr } from '@app/sales/rental-billing/rental-days';
import { docPatients, rentalSubtitle, rentalTitle } from '@app/sales/rental-billing/rental-billing.labels';

/** Statut d'avancement d'une ligne de contrat dans la modale ; UP_TO_DATE = passage sans échec mais sans aucune pièce créée (audit 31/10 #33). */
export type ContractInvoiceRowStatus = 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'UP_TO_DATE' | 'ERROR';

/** Ligne affichée dans la table de la modale */
export interface ContractInvoiceRow {
  contract: PreparedRentalContractInvoice;
  status: ContractInvoiceRowStatus;
  selected: boolean;
  // Lignes du dossier décochées (origin_order_l_id) : ni facturées ni créditées à ce passage, reprises au suivant.
  excluded: Set<number>;
  // Aperçu du serveur pour la sélection en cours (contract tant que rien n'est décoché) : montants et FSE en sont tirés.
  preview: PreparedRentalContractInvoice;
  previewLoading?: boolean;
  previewError?: string;
  expanded?: boolean;
  fseId?: number;
  errorMessage?: string;
}

/** Représentation unifiée d'un article ou d'un séjour de location pour la colonne Période / Détail */
export interface ContractPeriodDisplayItem {
  id: number;
  title: string; // intitulé du contrat de location, l'article pour une vente
  article: string | null; // l'article sous l'intitulé du contrat
  memo?: string | null;
  start: string | null; // premier jour couvert, jour de Paris « AAAA-MM-JJ »
  end: string | null; // dernier jour couvert, inclus
  periodsCount: number;
  isRental: boolean;
  qty: number;
  amountTaxi?: number;
}

/** Données injectées dans la modale */
export interface RentalInvoicingDialogData {
  eligibleInvoices: PreparedRentalContractInvoice[];
  unbillableOrders: UnbillableRentalContract[];
}

/** Pièces et anomalies rendues par le passage 2101 (rental/model.rs, réponse de RENTAL_INVOICING). */
// Références scellées (FAC-…, AV-…) rendues par le passage ; absentes d'un serveur plus ancien : l'id est montré à la place.
export interface PassageInvoice { parent_order_id: number; invoice_order_id: number; invoice_doc_ref?: string | null; fse_id: number | null; }
export interface PassageCredit { parent_order_id: number; corrects_invoice_id: number; credit_order_id: number; credit_doc_ref?: string | null; }
export interface PassageAnomaly { order_id: number; origin_order_l_id: number | null; blocked: boolean; message: string; }
export interface PassageReport { invoices: PassageInvoice[]; credits: PassageCredit[]; anomalies: PassageAnomaly[]; }

// Les bornes de prepare_invoicing sont des jours de Paris « AAAA-MM-JJ », start et end INCLUS (depuis le 27/10/2026) : affichées telles quelles,
// sans plus retirer de jour à la fin (avant : instants [début, fin) dont on montrait la veille).

/** Résultat retourné à la fermeture de la modale ; invoicedCount = dossiers avec au moins une pièce créée, upToDateCount = passés sans pièce. */
export interface RentalInvoicingDialogResult {
  invoicedCount: number;
  errorCount: number;
  upToDateCount: number;
  invoicedOrderIds: number[];
}

// Message de l'appelant (F4, page du dossier) : « réussi » ne compte que les dossiers qui ont reçu une pièce (#33).
export function invoicingOutcome(r: RentalInvoicingDialogResult | null | undefined): { kind: 'success' | 'error' | 'info'; text: string } | null {
  if (!r) return null;
  const upToDate = r.upToDateCount > 0 ? `${r.upToDateCount} déjà à jour (aucune pièce créée)` : '';
  if (r.invoicedCount > 0) return { kind: 'success', text: `Facturation réussie pour ${r.invoicedCount} location(s)` + [upToDate, r.errorCount > 0 ? `${r.errorCount} échec(s)` : ''].filter((x) => !!x).map((x) => ` (${x})`).join('') };
  if (r.errorCount > 0) return { kind: 'error', text: `Échec de facturation sur ${r.errorCount} location(s)` + (upToDate ? ` (${upToDate})` : '') };
  return r.upToDateCount > 0 ? { kind: 'info', text: `Aucune pièce créée : ${r.upToDateCount} location(s) déjà à jour` } : null;
}

// Ce qu'un aperçu montre et que le passage facturera : comparé avant le 2101 à l'aperçu refait (#33).
export function previewMark(c: PreparedRentalContractInvoice | null | undefined): string {
  if (!c) return '';
  const n = (v: any) => Number(v) || 0;
  return JSON.stringify([
    n(c.amount_taxx), n(c.amount_taxi), n(c.credit_taxx), n(c.credit_taxi), n(c.lines_count), c.start ?? null, c.end ?? null, c.billed_until ?? null,
    (c.lines ?? []).map((l) => [l.origin_order_l_id, l.start ?? null, l.end ?? null, n(l.qty), n(l.net_taxi)]),
    (c.credits ?? []).map((x) => [x.origin_order_l_id, x.corrects_invoice_id, x.start, x.end, n(x.qty), n(x.net_taxi)]),
    (c.anomalies ?? []).filter((a) => a.blocked).map((a) => [a.origin_order_l_id, a.message]),
  ]);
}

@Component({
  selector: 'app-rental-invoicing-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatCheckboxModule,
    RouterLink,
    SelectDateComponent,
  ],
  templateUrl: './rental-invoicing-dialog.component.html',
  styleUrls: ['./rental-invoicing-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RentalInvoicingDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<RentalInvoicingDialogComponent>);
  public data: RentalInvoicingDialogData = inject(MAT_DIALOG_DATA);
  private rentalFseService = inject(RentalFseService);
  private quotationsService = inject(QuotationsService);
  private loginService = inject(LoginService);
  private cdr = inject(ChangeDetectorRef);
  // Recalcul de l'aperçu d'un dossier après un clic sur ses lignes : différé, le précédent annulé (par dossier).
  private previewCalls = new Map<number, { timer?: ReturnType<typeof setTimeout>; sub?: Subscription }>();

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.previewCalls.forEach(c => { clearTimeout(c.timer); c.sub?.unsubscribe(); });
      this.untilSub?.unsubscribe();
    });
  }

  /** Lignes éligibles avec statut réactif */
  public rows: ContractInvoiceRow[] = [];
  /** Commandes non éligibles avec motif */
  public unbillableOrders: UnbillableRentalContract[] = [];

  /** Indicateur d'exécution en cours */
  public isProcessing = false;
  /** Indicateur que la facturation a été confirmée et lancée */
  public hasStarted = false;
  /** Indicateur de traitement terminé */
  public isFinished = false;

  /** Statistiques de progression */
  public processedCount = 0;
  public totalCount = 0;
  public successCount = 0;
  public errorCount = 0;
  public upToDateCount = 0;
  /** Aperçu refait au clic et différent de celui affiché : dit au-dessus de la liste, rien n'est facturé (#33). */
  public staleMessage: string | null = null;

  /** IDs facturés avec succès et envoyés au backend */
  public invoicedOrderIds: number[] = [];
  /** Mapping des FSE enfants sécurisées */
  private fseMapping: FseOrdersMapping = {};

  /** Panneau des non éligibles déplié */
  public showUnbillable = false;

  /** Bilan du passage : factures, avoirs et anomalies que le serveur a rendus */
  public passage: PassageReport | null = null;

  /** Jour du calcul de l'aperçu reçu à l'ouverture (aujourd'hui pour le serveur), « AAAA-MM-JJ » : plus tard, rien n'est dû. */
  public today = '';
  /** « Facturer jusqu'au » : jour du calcul de l'aperçu et du passage, dernier jour dû compris ; today = passage ordinaire. */
  public untilDay = '';
  public untilDate: Date | undefined;
  /** Dernier jour déjà facturé, le plus tardif des dossiers du dialogue ; null si aucun n'a encore de facture. */
  public lastBilledDay: string | null = null;
  /** Ce jour par dossier, quand ils diffèrent : dit par l'infobulle du bouton (#56, le passage n'a qu'un as_of pour tous). */
  public billedDays: { orderId: number; day: string | null }[] = [];
  public untilEnabled = false;
  public untilLoading = false;
  public untilError: string | null = null;
  private untilSub?: Subscription;
  // Tous les dossiers de l'ouverture, éligibles ou non : relus ensemble à chaque changement de « Facturer jusqu'au ».
  private orderIds: number[] = [];
  // Cases d'un dossier sorti de la liste à une date, retrouvées s'il y revient.
  private rowStates = new Map<number, { selected: boolean; excluded: Set<number> }>();

  readonly money = money;
  readonly qtyFr = qtyFr;

  ngOnInit(): void {
    this.unbillableOrders = this.data.unbillableOrders ?? [];
    const eligible = this.data.eligibleInvoices ?? [];
    this.today = eligible[0]?.as_of ?? todayStr();
    this.untilDay = this.today;
    this.untilDate = localDate(this.today);
    this.lastBilledDay = eligible.reduce<string | null>((m, c) => (c.billed_until && (!m || c.billed_until > m) ? c.billed_until : m), null);
    this.billedDays = eligible.map(c => ({ orderId: c.order_id, day: c.billed_until ?? null }));
    this.orderIds = [...eligible.map(c => c.order_id), ...this.unbillableOrders.map(u => u.order_id)];
    // Une date plus ancienne ne fait que retirer des périodes : sans rien d'éligible aujourd'hui, le champ ne servirait à rien.
    this.untilEnabled = eligible.length > 0;
    this.rows = eligible.map(contract => ({
      contract,
      status: 'PENDING',
      selected: true,
      excluded: new Set<number>(),
      preview: contract,
    }));
    this.totalCount = this.rows.length;

    // Mode 2 étapes : la modale s'ouvre d'abord en prévisualisation.
    // L'utilisateur visualise les contrats éligibles et confirme avant tout lancement réel.
    if (this.totalCount === 0) {
      this.isFinished = true;
    }
  }

  /**
   * Nombre de contrats actuellement cochés pour la facturation
   */
  get selectedCount(): number {
    return this.rows.filter(r => r.selected).length;
  }

  /**
   * Montant total TTC des contrats sélectionnés
   */
  get totalEligibleAmount(): number {
    return this.rows
      .filter(r => r.selected)
      .reduce((sum, r) => sum + (Number(r.preview.amount_taxi) || 0), 0);
  }

  /**
   * Tous les contrats sont-ils cochés, toutes leurs lignes comprises ?
   */
  get isAllSelected(): boolean {
    return this.rows.length > 0 && this.rows.every(r => r.selected && r.excluded.size === 0);
  }

  /**
   * Une partie des contrats ou de leurs lignes seulement est cochée
   */
  get isPartiallySelected(): boolean {
    return this.selectedCount > 0 && !this.isAllSelected;
  }

  /** Lignes décochées dans les dossiers cochés. */
  get excludedCount(): number {
    return this.rows.filter(r => r.selected).reduce((n, r) => n + r.excluded.size, 0);
  }

  // Confirmation interdite tant qu'un dossier coché n'a pas l'aperçu de sa sélection : la FSE et le passage doivent dire la même chose.
  get previewPending(): boolean {
    return this.untilLoading || !!this.untilError || this.rows.some(r => r.selected && (r.previewLoading || !!r.previewError));
  }

  /** as_of envoyé à l'aperçu et au passage : null tant que « Facturer jusqu'au » vaut aujourd'hui. */
  get asOf(): string | null {
    return this.untilDay && this.untilDay !== this.today ? this.untilDay : null;
  }

  public onUntilChange(d: Date | string | null | undefined): void {
    this.setUntil(pickedDateStr(d));
  }

  // JP : ne facturer que les avoirs d'un mois déjà facturé, le mois en cours attendant sa fin pour partir en entier.
  public untilLastBilled(): void {
    if (this.lastBilledDay) this.setUntil(this.lastBilledDay);
  }

  public untilToday(): void {
    this.setUntil(this.today);
  }

  // Audit 31/10 #56 : un seul « Facturer jusqu'au » pour tous les dossiers ; ceux facturés moins loin y sont facturés jusqu'à ce jour, l'infobulle le dit dossier par dossier.
  public get lastBilledTip(): string {
    if (!this.lastBilledDay) return 'Aucune période encore facturée';
    const base = `Facturer jusqu'au ${frDay(this.lastBilledDay)}, le plus tardif des dossiers : seuls les avoirs des périodes déjà facturées partent`;
    const behind = this.billedDays.filter(b => b.day !== this.lastBilledDay);
    if (!behind.length) return base;
    return `${base}, sauf pour ${behind.length > 1 ? 'les dossiers' : 'le dossier'} facturé${behind.length > 1 ? 's' : ''} moins loin, qui ${behind.length > 1 ? 'seront facturés' : 'sera facturé'} jusqu'à ce jour :\n`
      + behind.map(b => `n° ${b.orderId} : ${b.day ? 'déjà facturé jusqu\'au ' + frDay(b.day) : 'rien de facturé'}`).join('\n');
  }

  public get billedDaysDiffer(): boolean {
    return this.billedDays.some(b => b.day !== this.lastBilledDay);
  }

  // Tous les dossiers relus au nouveau jour ; jamais un jour futur (le serveur le refuserait : on ne facture que l'échu).
  private setUntil(day: string | null): void {
    if (this.hasStarted) return;
    this.untilSub?.unsubscribe();
    this.untilLoading = false;
    if (!day) {
      this.untilError = 'Date illisible';
      this.cdr.markForCheck();
      return;
    }
    this.untilDate = localDate(day);
    // Jour futur : montré dans le champ avec le refus, mais le jour retenu (sous-titre, lignes) reste le précédent.
    if (day > this.today) {
      this.untilError = `Au plus tard aujourd'hui, le ${frDay(this.today)} : seules les périodes échues se facturent`;
      this.cdr.markForCheck();
      return;
    }
    this.untilDay = day;
    this.untilError = null;
    this.untilLoading = true;
    this.untilSub = this.quotationsService.prepareRentalInvoicing(this.orderIds, [], this.asOf).subscribe({
      next: r => {
        this.untilLoading = false;
        this.applyPreparation(r);
        this.cdr.markForCheck();
      },
      error: err => {
        this.untilLoading = false;
        this.untilError = typeof err?.error === 'string' && err.error ? err.error : (err?.message ?? String(err));
        this.cdr.markForCheck();
      },
    });
    this.cdr.markForCheck();
  }

  // Les cases de chaque dossier survivent au changement de jour (lignes encore présentes) ; une sélection partielle redemande son aperçu.
  private applyPreparation(r: PrepareRentalInvoicingResponse): void {
    this.rows.forEach(row => this.rowStates.set(row.contract.order_id, { selected: row.selected, excluded: row.excluded }));
    this.previewCalls.forEach(c => { clearTimeout(c.timer); c.sub?.unsubscribe(); });
    this.previewCalls.clear();
    this.rows = (r.eligible_invoices ?? []).map(contract => {
      const kept = this.rowStates.get(contract.order_id);
      const ids = new Set((contract.lines ?? []).map(l => l.origin_order_l_id));
      let excluded = new Set([...(kept?.excluded ?? [])].filter(id => ids.has(id)));
      let selected = kept?.selected ?? true;
      if (ids.size > 0 && excluded.size === ids.size) {
        selected = false;
        excluded = new Set<number>();
      }
      return { contract, status: 'PENDING' as ContractInvoiceRowStatus, selected, excluded, preview: contract };
    });
    this.rows.forEach(row => this.refreshPreview(row));
    this.unbillableOrders = r.unbillable_orders ?? [];
    this.totalCount = this.rows.length;
  }

  /**
   * Pourcentage global d'avancement pour la barre de progression
   */
  get progressPercent(): number {
    if (this.totalCount === 0) return 100;
    return Math.round((this.processedCount / this.totalCount) * 100);
  }

  /**
   * Coche ou décoche l'ensemble des contrats éligibles
   */
  public toggleSelectAll(event: MatCheckboxChange): void {
    if (this.hasStarted) return;
    const checked = event.checked;
    this.rows.forEach(r => {
      r.selected = checked;
      r.excluded.clear();
      this.refreshPreview(r);
    });
    this.cdr.markForCheck();
  }

  /**
   * Bascule la sélection d'un contrat : coché en entier, il se décoche ; sinon (rien ou une partie de ses lignes) il se coche en entier
   */
  public toggleRowSelection(row: ContractInvoiceRow): void {
    if (this.hasStarted) return;
    row.selected = !(row.selected && row.excluded.size === 0);
    row.excluded.clear();
    this.refreshPreview(row);
    this.cdr.markForCheck();
  }

  public isLineChecked(row: ContractInvoiceRow, lineId: number): boolean {
    return row.selected && !row.excluded.has(lineId);
  }

  // Une ligne du dossier peut donner plusieurs lignes de facture (fraction, palier) : la case vaut pour toutes, par origin_order_l_id.
  public toggleLine(row: ContractInvoiceRow, lineId: number): void {
    if (this.hasStarted) return;
    const ids = [...new Set((row.contract.lines ?? []).map(l => l.origin_order_l_id))];
    if (!row.selected) {
      row.selected = true;
      row.excluded = new Set(ids.filter(id => id !== lineId));
    } else if (row.excluded.has(lineId)) {
      row.excluded.delete(lineId);
    } else {
      row.excluded.add(lineId);
    }
    // Plus aucune ligne cochée : le dossier entier est décoché.
    if (ids.every(id => row.excluded.has(id))) {
      row.selected = false;
      row.excluded.clear();
    }
    this.refreshPreview(row);
    this.cdr.markForCheck();
  }

  // Montants d'une sélection partielle : redemandés au serveur (TVA par taux, avoirs), jamais additionnés ici ; le dernier montant reste affiché pendant le calcul.
  private refreshPreview(row: ContractInvoiceRow): void {
    const orderId = row.contract.order_id;
    const call = this.previewCalls.get(orderId) ?? {};
    clearTimeout(call.timer);
    call.sub?.unsubscribe();
    row.previewError = undefined;
    if (!row.selected || row.excluded.size === 0) {
      row.preview = row.contract;
      row.previewLoading = false;
      this.previewCalls.delete(orderId);
      return;
    }
    row.previewLoading = true;
    const excluded = [...row.excluded];
    call.timer = setTimeout(() => {
      call.sub = this.quotationsService.prepareRentalInvoicing([orderId], excluded, this.asOf).subscribe({
        next: r => {
          const c = r.eligible_invoices.find(x => x.order_id === orderId);
          if (c) row.preview = c;
          else row.previewError = r.unbillable_orders.find(u => u.order_id === orderId)?.reason || 'aperçu du serveur sans ce dossier';
          row.previewLoading = false;
          this.cdr.markForCheck();
        },
        error: err => {
          row.previewLoading = false;
          row.previewError = typeof err?.error === 'string' && err.error ? err.error : (err?.message ?? String(err));
          this.cdr.markForCheck();
        },
      });
    }, 300);
    this.previewCalls.set(orderId, call);
  }

  /**
   * Déplie ou replie le détail des lignes et périodes facturées
   */
  public toggleExpandRow(row: ContractInvoiceRow): void {
    row.expanded = !row.expanded;
    this.cdr.markForCheck();
  }

  /**
   * Extrait et sépare les données des articles et séjours de location d'un contrat :
   * - Libellé Article (avec mémo séjour / patient si présent)
   * - Dates de début et fin de période
   * - Nombre de périodes / jours
   */
  public getDisplayItems(contract: PreparedRentalContractInvoice): ContractPeriodDisplayItem[] {
    if (contract.lines && contract.lines.length > 0) {
      return contract.lines.map(line => ({
        id: line.origin_order_l_id,
        title: rentalTitle(line) || 'Location',
        article: rentalSubtitle(line),
        memo: line.memo,
        start: line.start ?? null,
        end: line.end ?? null,
        periodsCount: line.periods_count ?? 1,
        isRental: line.line_type === 'RENTAL',
        qty: Number(line.qty) || 1,
        amountTaxi: Number(line.net_taxi) || 0,
      }));
    }

    // Le serveur rend une ligne (net arrondi compris) pour chaque période : sans lignes, rien à montrer, aucun montant calculé ici.
    return [];
  }

  /**
   * Compte les périodes (rangs) facturées sur un contrat, toutes lignes de location confondues.
   * Exemple : pour le contrat 457230, totalise les rangs des 28 séjours.
   */
  public getTotalPeriods(contract: PreparedRentalContractInvoice): number {
    const items = this.getDisplayItems(contract);
    const sum = items
      .filter(i => i.isRental)
      .reduce((acc, i) => acc + (i.periodsCount || 0), 0);
    return sum > 0 ? sum : (contract.periods?.length || 1);
  }

  /**
   * Retourne le nombre de séjours / lignes de location sur le contrat.
   */
  public getRentalLinesCount(contract: PreparedRentalContractInvoice): number {
    const items = this.getDisplayItems(contract);
    return items.filter(i => i.isRental).length;
  }

  /** Total TTC (négatif) des avoirs que le passage posera, tel que le serveur le calcule ; null s'il ne le rend pas. */
  public creditsTotal(contract: PreparedRentalContractInvoice): number | null {
    return contract.credit_taxi ?? null;
  }

  public creditsTotalHt(contract: PreparedRentalContractInvoice): number | null {
    return contract.credit_taxx ?? null;
  }

  public periodsLabel(n: number | null | undefined): string {
    const k = Number(n) || 0;
    return `${k} période${k > 1 ? 's' : ''}`;
  }

  /** « du 05/01/2026 au 11/01/2026 », start et end inclus ; vide sans bornes. */
  public rangeLabel(start: string | null | undefined, end: string | null | undefined): string {
    return start && end ? `du ${frDay(start)} au ${frDay(end)}` : '';
  }

  public headerRange(contract: PreparedRentalContractInvoice): string {
    return this.rangeLabel(contract.start, contract.end);
  }

  public lineRange(l: PreparedRentalSimulationLine): string {
    return this.rangeLabel(l.start, l.end);
  }

  public subPeriodRange(sp: PreparedSinglePeriodInfo): string {
    return `${frShort(sp.start)} → ${frShort(sp.end)}`;
  }

  // Un avoir de vente (annulation 2106) n'a pas de période : « Vente annulée », jamais « du  au ».
  public creditRange(c: PreparedRentalCredit): string {
    return c.start == null ? 'Vente annulée' : this.rangeLabel(c.start, c.end);
  }

  public day(v: string | null | undefined): string {
    return frDay(businessDayOf(v));
  }

  // billed_until = dernier jour déjà facturé, inclus, montré tel quel (avant : last_billed_date, instant du lendemain dont on montrait la veille).
  public billedUntilDay(contract: PreparedRentalContractInvoice): string {
    return frDay(contract.billed_until);
  }

  public blockingAnomalies(anomalies: RentalAnomaly[] | undefined): number {
    return (anomalies ?? []).filter(a => a.blocked).length;
  }

  /** Libellé de la ligne d'un dossier citée par une anomalie (null = anomalie du dossier entier). */
  public anomalyLine(contract: PreparedRentalContractInvoice, a: RentalAnomaly): string {
    if (a.origin_order_l_id == null) return 'Dossier';
    const l = contract.lines?.find(x => x.origin_order_l_id === a.origin_order_l_id);
    return `Ligne ${a.origin_order_l_id}${l?.product_name ? ' · ' + l.product_name : ''}`;
  }

  public lineTitle(l: PreparedRentalSimulationLine): string | null { return rentalTitle(l); }
  public lineArticle(l: PreparedRentalSimulationLine): string | null { return rentalSubtitle(l); }

  /**
   * Ferme la modale sans facturer (Annuler)
   */
  public onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Lance le traitement séquentiel Area sur les contrats éligibles sélectionnés,
   * puis finalise côté backend avec les contrats réussis.
   */
  public async startInvoicingProcess(): Promise<void> {
    const selectedRows = this.rows.filter(r => r.selected);
    if (selectedRows.length === 0 || this.previewPending || this.checking) return;
    const excludedLineIds = selectedRows.flatMap(r => [...r.excluded]);
    if (!(await this.previewStillValid(selectedRows, excludedLineIds))) return;

    this.hasStarted = true;
    this.isProcessing = true;
    this.isFinished = false;
    this.processedCount = 0;
    this.totalCount = selectedRows.length;
    this.successCount = 0;
    this.errorCount = 0;
    this.upToDateCount = 0;
    this.fseMapping = {};
    this.invoicedOrderIds = [];
    this.passage = null;
    this.cdr.markForCheck();

    const successfulOrderIds: number[] = [];

    // Boucle séquentielle (respect de l'accès exclusif à la CPS)
    for (const row of selectedRows) {
      row.status = 'PROCESSING';
      this.cdr.markForCheck();

      try {
        // Dossier qui ne porte que des avoirs partiels : aucune facture, donc aucune FSE à sécuriser.
        if (row.contract.is_fse && row.preview.lines_count > 0) {
          // Sécurisation Area via le service FSE, d'après l'aperçu sans les lignes décochées (le passage les écarte aussi)
          const result = await firstValueFrom(this.rentalFseService
            .processSingleFseRental$(
              { id: row.contract.order_id, data: { fse_id: row.contract.fse_template_id } },
              row.preview,
            ));

          if (result && result.fseId > 0) {
            row.status = 'SUCCESS';
            row.fseId = result.fseId;
            this.fseMapping[row.contract.order_id] = result.fseId;
            successfulOrderIds.push(row.contract.order_id);
            this.successCount++;
          } else {
            row.status = 'ERROR';
            row.errorMessage = 'Identifiant FSE manquant en retour de sécurisation';
            this.errorCount++;
          }
        } else {
          // Contrat standard sans FSE
          row.status = 'SUCCESS';
          successfulOrderIds.push(row.contract.order_id);
          this.successCount++;
        }
      } catch (err: any) {
        console.error(`[RentalInvoicingDialog] Échec sur contrat #${row.contract.order_id}:`, err);
        row.status = 'ERROR';
        row.errorMessage = err?.message ?? String(err);
        this.errorCount++;
      }

      this.processedCount++;
      this.cdr.markForCheck();
    }

    // Étape finale : Finalisation côté serveur pour les seuls contrats ayant réussi
    if (successfulOrderIds.length > 0) {
      const userId = this.loginService.loggeduser?.id;
      let res: any = null;
      try {
        res = await firstValueFrom(this.quotationsService
          .postRentalInvoicingEvent(successfulOrderIds, userId, this.fseMapping, excludedLineIds, this.asOf));
        if (res?.success === false) {
          throw new Error(res?.msg || res?.error_msg || 'passage refusé par le serveur');
        }
      } catch (err: any) {
        console.error('[RentalInvoicingDialog] Erreur finale RENTAL_INVOICING:', err);
        res = null;
        // En cas d'erreur globale (ex: 500, réseau cassé)
        // On rollback tout ce qu'on avait réussi
        const allFseIdsToRollback = selectedRows.filter(r => r.status === 'SUCCESS' && r.fseId).map(r => r.fseId!);
        for (const row of selectedRows) {
          if (row.status === 'SUCCESS') {
            row.status = 'ERROR';
            row.errorMessage = `Erreur backend (globale) : ${err?.message ?? err}${row.fseId ? ` ; FSE n° ${row.fseId} : annulation demandée à Area` : ''}`;
          }
        }
        this.errorCount += this.successCount;
        this.successCount = 0;
        this.invoicedOrderIds = [];
        await this.rollbackFses(selectedRows, allFseIdsToRollback);
      }

      if (res) {
        const fseIdsToRollback = this.settlePassage(selectedRows, res.data);
        this.invoicedOrderIds = selectedRows.filter(r => r.status === 'SUCCESS').map(r => r.contract.order_id);
        await this.rollbackFses(selectedRows, fseIdsToRollback);
      }
    }

    this.isProcessing = false;
    this.isFinished = true;
    this.cdr.markForCheck();
  }

  /** Aperçu refait au clic en cours : la confirmation attend sa réponse. */
  public checking = false;

  // Audit 31/10 #33 : l'aperçu est refait au clic, quel que soit son âge ; un dossier qui a changé depuis l'affichage (ligne livrée, facture posée ailleurs)
  // n'est jamais facturé sans être revu : la liste est relue, le motif dit, et l'utilisateur confirme de nouveau.
  private async previewStillValid(rows: ContractInvoiceRow[], excludedLineIds: number[]): Promise<boolean> {
    this.checking = true;
    this.staleMessage = null;
    this.cdr.markForCheck();
    try {
      const fresh = await firstValueFrom(this.quotationsService.prepareRentalInvoicing(rows.map(r => r.contract.order_id), excludedLineIds, this.asOf));
      const changed = rows.filter(r => previewMark(fresh?.eligible_invoices?.find(c => c.order_id === r.contract.order_id)) !== previewMark(r.preview)).map(r => r.contract.order_id);
      if (!changed.length) return true;
      this.staleMessage = `L'aperçu a changé depuis son affichage pour ${changed.length > 1 ? 'les dossiers' : 'le dossier'} n° ${changed.join(', ')} (livraison, reprise ou facture enregistrée entre-temps) : rien n'est facturé, vérifiez la liste relue puis confirmez de nouveau.`;
      this.setUntil(this.untilDay);
      return false;
    } catch (err: any) {
      this.staleMessage = `Aperçu non relu avant la facturation, rien n'est facturé : ${typeof err?.error === 'string' && err.error ? err.error : (err?.message ?? String(err))}`;
      return false;
    } finally {
      this.checking = false;
      this.cdr.markForCheck();
    }
  }

  // Une FSE sécurisée n'est gardée que si le 2101 a créé sa FACTURE_FSE ; dossier en échec ou passage sans cette facture : FSE à annuler dans Area.
  private settlePassage(rows: ContractInvoiceRow[], data: any): number[] {
    const failed: any[] = Array.isArray(data?.failed_invoices) ? data.failed_invoices : [];
    const generated: any[] = Array.isArray(data?.generated_invoices) ? data.generated_invoices : [];
    const list = (v: any) => (Array.isArray(v) ? v : []);
    this.passage = { invoices: generated, credits: list(data?.generated_credits), anomalies: list(data?.anomalies) };
    const fseIdsToRollback: number[] = [];
    for (const row of rows) {
      if (row.status !== 'SUCCESS') continue;
      const orderId = row.contract.order_id;
      const failure = failed.find(f => f?.order_id === orderId);
      let motif: string | null = null;
      if (failure) {
        motif = `Échec du passage de facturation : ${failure.error ?? 'motif non renvoyé par le serveur'}`;
      } else if (row.fseId && !generated.some(g => g?.parent_order_id === orderId && g?.fse_id === row.fseId)) {
        motif = `Le passage n'a créé aucune FACTURE_FSE pour la FSE n° ${row.fseId}`;
      }
      if (motif === null) {
        // #33 : passé sans échec mais sans facture ni avoir, le dossier était déjà à jour ; il ne compte pas comme facturé.
        if (!generated.some(g => g?.parent_order_id === orderId) && !list(data?.generated_credits).some((c: any) => c?.parent_order_id === orderId)) {
          row.status = 'UP_TO_DATE';
          this.successCount--;
          this.upToDateCount++;
        }
        continue;
      }
      row.status = 'ERROR';
      row.errorMessage = row.fseId ? `${motif} ; FSE n° ${row.fseId} : annulation demandée à Area` : motif;
      if (row.fseId) fseIdsToRollback.push(row.fseId);
      this.successCount--;
      this.errorCount++;
    }
    return fseIdsToRollback;
  }

  // Attendue avant la fin : le lecteur CPS ne doit pas servir à un nouveau passage pendant l'annulation ; un échec se dit sur la ligne, à reprendre à la main.
  private async rollbackFses(rows: ContractInvoiceRow[], fseIds: number[]): Promise<void> {
    if (fseIds.length === 0) return;
    console.warn('[RentalInvoicingDialog] Rollback Area déclenché pour', fseIds.length, 'FSEs');
    try {
      await firstValueFrom(this.rentalFseService.rollbackAreaFses$(fseIds));
    } catch (err: any) {
      console.error('[RentalInvoicingDialog] Échec du rollback Area :', err);
      for (const row of rows) {
        if (row.fseId && fseIds.includes(row.fseId)) {
          row.errorMessage = `${row.errorMessage ?? ''} ; ÉCHEC de l'annulation dans Area (${err?.message ?? err}) : FSE n° ${row.fseId} à vérifier et annuler à la main`;
        }
      }
    }
  }

  /** Pièces et anomalies du passage pour un dossier. */
  public passageOf(orderId: number): PassageReport {
    const p = this.passage;
    return {
      invoices: (p?.invoices ?? []).filter(i => i?.parent_order_id === orderId),
      credits: (p?.credits ?? []).filter(c => c?.parent_order_id === orderId),
      anomalies: (p?.anomalies ?? []).filter(a => a?.order_id === orderId),
    };
  }

  public get passageIsEmpty(): boolean {
    const p = this.passage;
    return !p || p.invoices.length + p.credits.length + p.anomalies.length === 0;
  }

  public docRef(ref: string | null | undefined, id: number): string {
    return ref?.trim() || `n° ${id}`;
  }

  /** Pour qui est la facture créée : mémos des lignes de l'aperçu passé (une facture au plus par dossier et par passage). */
  public invoicePatients(row: ContractInvoiceRow): { text: string; title: string } {
    return docPatients(uniqueMemos((row.preview.lines ?? []).map(l => l.memo)));
  }

  /** Pour qui est l'avoir créé : mémos des lignes d'avoir de l'aperçu passé sur la même facture corrigée. */
  public creditPatients(row: ContractInvoiceRow, invoiceId: number): { text: string; title: string } {
    return docPatients(uniqueMemos((row.preview.credits ?? []).filter(c => c.corrects_invoice_id === invoiceId).map(c => c.memo)));
  }

  /** Référence de la facture corrigée par un avoir, lue dans l'aperçu du dossier. */
  public correctsRef(contract: PreparedRentalContractInvoice, invoiceId: number): string {
    return contract.credits?.find(c => c.corrects_invoice_id === invoiceId)?.corrects_doc_ref ?? `n° ${invoiceId}`;
  }

  // Clic simple sur une pièce créée : la modale se ferme avec son bilan (la liste se relit) et la navigation suit ; Ctrl+clic ouvre un onglet.
  public followDoc(e: MouseEvent): void {
    if (isPlainLeftClick(e)) this.onClose();
  }

  /**
   * Ferme la modale et retourne le bilan de la facturation
   */
  public onClose(): void {
    const result: RentalInvoicingDialogResult = {
      invoicedCount: this.invoicedOrderIds.length,
      errorCount: this.errorCount,
      upToDateCount: this.upToDateCount,
      invoicedOrderIds: this.invoicedOrderIds,
    };
    this.dialogRef.close(result);
  }

  /**
   * Alterne l'affichage de la section des contrats non éligibles
   */
  public toggleUnbillable(): void {
    this.showUnbillable = !this.showUnbillable;
    this.cdr.markForCheck();
  }
}

// Jour « AAAA-MM-JJ » en Date locale pour le sélecteur (jamais new Date('AAAA-MM-JJ'), minuit UTC).
function localDate(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Mémos non vides, sans doublon, dans l'ordre des lignes.
function uniqueMemos(memos: (string | null | undefined)[]): string[] {
  return [...new Set(memos.map(m => m?.trim() ?? '').filter(m => !!m))];
}
